// vorgangFuer.ts — Läuft ein neuer Vorgang auf die Person oder auf ihre Firma?
//
// PO 01.10.2026: „Wenn ich auf ‚Neuer Vorgang' gehe, dann soll er auch mit der
// Firma starten (wenn Sonstige); wenn Kunde oder Interessent, dann Person — ich
// kann es ja ändern."
//
// EINE Regel für zwei Stellen: das Fenster „Kontakt zuweisen" (Vorbelegung von
// „privat | geschäftlich", #1969) und der Erstell-Flow „Neuer Vorgang"
// (`VorgangCreateFlowWindow`, alle Einstiege). Beide fragen hier, damit sie
// nicht auseinanderlaufen. Rein, ohne React/Fetch — vitest-testbar.

import type { KontaktStatusGruppe } from "../../../services/kontaktSucheService";
import { derivePersonEmployment, type EmpRelation } from "../../../utils/derivePersonEmployment";
import type { KontaktRef, LinkSlots } from "../detail/linkSlots";

export type VorgangFirma = { id: number; name: string };

/**
 * Eine vorab getroffene Wahl („Kontakt zuweisen" → Weiter): gilt im Erstell-Flow
 * wie von Hand gewählt. `firma` nennt bei „firma" die gewählte Firma.
 */
export type VorgangFuerVorgabe = {
    auf: "person" | "firma";
    firma?: { id: number; displayName: string } | null;
};

/**
 * Gruppe des Primär-Status aus dem Langtext — dieselbe Regel wie im Backend
 * (`kontaktStatusGruppe`) und in `kisGruppe`: nur „Kunde" und „Interessent"
 * sind eigene Gruppen, alles andere (auch kein Status) ist „Sonstige".
 */
export function statusGruppeAusName(statusName: string | null | undefined): KontaktStatusGruppe {
    const s = (statusName ?? "").trim();
    if (s === "Kunde") return "kunde";
    if (s === "Interessent") return "interessent";
    return "sonstige";
}

/**
 * Die Regel selbst: Eine Person mit Firmenbezug, die weder Kunde noch
 * Interessent ist (Status „Sonstige" — der Ansprechpartner), startet mit der
 * Firma. Kunde/Interessent oder keine Firma → die Person.
 */
export function vorgangStartetMitFirma(
    statusGruppe: KontaktStatusGruppe | null | undefined,
    firmen: readonly unknown[] | null | undefined,
): boolean {
    return (statusGruppe ?? "sonstige") === "sonstige" && (firmen?.length ?? 0) > 0;
}

/**
 * Die verbundenen Firmen einer Person aus dem Kontakt-Detail
 * (`GET /contact/:id?include=relations`) — beide Richtungen, weil die Firma
 * eines Ansprechpartners im Bestand fast immer `src` und die Person `dest` ist.
 *
 * Wie die Zuweisen-Suche (`firmen[]`, T-26) zählt JEDE Verbindung zu einer
 * Firma, auch der reine „Mensch im Unternehmen". Die ausdrücklich markierte
 * Hauptarbeitgeberin steht vorn (= „führende" Firma), sonst bleibt die
 * Reihenfolge der Relationen. An einer Firma (oder Sonstigen) gibt es keine.
 */
export function firmenAusKontaktDetail(detail: unknown): VorgangFirma[] {
    const d = (detail ?? {}) as {
        type?: { type?: string } | null;
        relationsSrc?: EmpRelation[] | null;
        relations?: EmpRelation[] | null;
        relationsDest?: EmpRelation[] | null;
    };
    if (String(d.type?.type ?? "") !== "Person") return [];
    const zeilen = derivePersonEmployment(d.relationsSrc ?? d.relations, d.relationsDest, {
        inklusiveMenschImUnternehmen: true,
    }).filter((e) => e.companyId != null);
    const vorn = zeilen.filter((e) => e.istHauptarbeitgeber);
    const rest = zeilen.filter((e) => !e.istHauptarbeitgeber);
    return [...vorn, ...rest].map((e) => ({ id: e.companyId as number, name: e.companyName }));
}

/**
 * Womit startet der Vorgang für eine Person? `null` = auf die Person, sonst
 * die Firmen-Id. Eine Wahl von Hand (bzw. die aus „Kontakt zuweisen"
 * mitgegebene) schlägt die Status-Regel — wie beim Hauptverantwortlichen
 * (`hvVonHand`). `wunschFirmaId` wählt bei mehreren Firmen eine bestimmte.
 */
export function vorgangStartFirmaId(p: {
    statusGruppe: KontaktStatusGruppe | null | undefined;
    firmen: readonly VorgangFirma[];
    vonHand?: "person" | "firma" | null;
    wunschFirmaId?: number | null;
}): number | null {
    if (p.firmen.length === 0) return null;
    const aufFirma = p.vonHand ? p.vonHand === "firma" : vorgangStartetMitFirma(p.statusGruppe, p.firmen);
    if (!aufFirma) return null;
    const wunsch = p.wunschFirmaId != null ? p.firmen.find((f) => f.id === p.wunschFirmaId) : undefined;
    return (wunsch ?? p.firmen[0]).id;
}

/**
 * Legt den Vorgang auf die Person oder eine ihrer Firmen um. Läuft er auf die
 * Firma, bleibt die Person als weiterer Kontakt beteiligt (Position 1) — sie
 * hat schließlich geschrieben bzw. angerufen. Andere, vom Nutzer gewählte
 * Kontakte bleiben stehen; die übrigen Firmen der Person fallen heraus (sie
 * kamen nur über diesen Umschalter hinein). Risiken und Verträge bleiben
 * unangetastet — der Umschalter ist keine Kontakt-NEUwahl.
 */
export function slotsMitVorgangAuf(
    prev: LinkSlots,
    person: KontaktRef,
    firma: KontaktRef | null,
    alleFirmenIds: readonly number[],
): LinkSlots {
    const raus = new Set<number>([person.id, ...alleFirmenIds]);
    const rest = (prev.kontakte ?? []).filter((k) => !raus.has(k.id));
    const kontakte = firma ? [firma, person, ...rest] : [person, ...rest];
    return { ...prev, kontakt: kontakte[0], kontakte };
}
